import { useEffect, useMemo, useState } from "react";
import { IconExternalLink, IconRefresh } from "@tabler/icons-react";

import { loadGmailMailboxes } from "../../core-api/gmail";

type TestMailScenarioId =
  | "STANDARD_RFQ"
  | "URGENT_RFQ"
  | "MULTI_PRODUCT"
  | "MISSING_DETAILS"
  | "ACADEMIC_RESEARCH"
  | "ENTERPRISE_PROCUREMENT"
  | "COMPLIANCE_REVIEW"
  | "FOLLOW_UP"
  | "NON_INQUIRY"
  | "BILINGUAL_RFQ";

interface TestMailScenario {
  id: TestMailScenarioId;
  label: string;
  purpose: string;
  subject: string;
  body: string;
}

interface GeneratedTestMail {
  reference: string;
  subject: string;
  body: string;
}

export const DEVTESTER_TEST_MAIL_SCENARIOS: readonly TestMailScenario[] = [
  {
    id: "STANDARD_RFQ",
    label: "标准询价",
    purpose: "标准产品、CAS、数量和目的地",
    subject: "Request for quotation - Benzyl alcohol, CAS 100-51-6",
    body: `Hello Sales Team,

Please quote the following product:

Product: Benzyl alcohol
CAS: 100-51-6
Quantity: 500 g
Purity: >= 99.5%
Ship to: Boston, MA, United States

Please include price, lead time, pack size, and shipping terms.

Best regards,
Alex Morgan
Northstar Research LLC`,
  },
  {
    id: "URGENT_RFQ",
    label: "紧急询价",
    purpose: "高优先级和明确交付期限",
    subject: "URGENT RFQ - Acetonitrile, CAS 75-05-8",
    body: `Hello,

We urgently need a quotation for Acetonitrile (CAS 75-05-8), HPLC grade, 4 x 4 L.
The material must arrive in Singapore within 7 calendar days.

Please confirm stock, earliest dispatch date, price, and available certificates today.

Regards,
Maya Chen
Vertex Analytical Pte. Ltd.`,
  },
  {
    id: "MULTI_PRODUCT",
    label: "多产品询价",
    purpose: "一封邮件包含多个产品行",
    subject: "RFQ for three laboratory reagents",
    body: `Dear Sales,

Please provide a combined quotation for:

1. Benzyl alcohol, CAS 100-51-6, 1 kg, >= 99%
2. Salicylic acid, CAS 69-72-7, 2 kg, reagent grade
3. Caffeine, CAS 58-08-2, 500 g, reference standard

Ship to Berlin, Germany. Please state lead time separately for each item.

Kind regards,
Daniel Weber
Helix Laboratory GmbH`,
  },
  {
    id: "MISSING_DETAILS",
    label: "信息不完整",
    purpose: "缺少 CAS、规格和数量，测试澄清流程",
    subject: "Looking for a specialty reagent",
    body: `Hi,

Can you supply high-purity phosphatidylcholine for a formulation project?
We have not finalized the grade or package size yet.

Please let me know what options are available.

Thanks,
Jamie`,
  },
  {
    id: "ACADEMIC_RESEARCH",
    label: "高校科研",
    purpose: "高校实验室、小包装、科研用途",
    subject: "Academic quotation request - Caffeine standard",
    body: `Dear Team,

I am requesting an academic quotation for Caffeine, CAS 58-08-2, 100 mg analytical reference standard.
This will be used for non-clinical chromatography method development at our university laboratory.

Please include academic pricing and certificate availability.

Sincerely,
Dr. Priya Nair
Department of Chemistry
Westbridge University`,
  },
  {
    id: "ENTERPRISE_PROCUREMENT",
    label: "企业采购",
    purpose: "批量采购、合规文件和付款条款",
    subject: "Procurement RFQ - annual supply requirement",
    body: `Dear Commercial Team,

Our procurement department is evaluating suppliers for an annual requirement of Salicylic acid (CAS 69-72-7), approximately 250 kg per quarter.

Please provide tiered pricing, Incoterms, payment terms, country of origin, SDS, COA, ISO certificates, and production lead time.

Regards,
Elena Rossi
Strategic Procurement
Ardent Materials S.p.A.`,
  },
  {
    id: "COMPLIANCE_REVIEW",
    label: "合规审查",
    purpose: "最终用途和跨境合规信息需要复核",
    subject: "Quotation and export documentation request",
    body: `Hello Sales,

Please quote 5 kg of Sodium borohydride, CAS 16940-66-2, for industrial process development.
Destination is Dubai, United Arab Emirates.

Please advise which end-use statement, export documents, and customer verification records are required before order acceptance.

Regards,
Omar Haddad
Process Development Consultant`,
  },
  {
    id: "FOLLOW_UP",
    label: "跟进邮件",
    purpose: "Re: 主题和历史询价引用",
    subject: "Re: RFQ - Benzyl alcohol, CAS 100-51-6",
    body: `Hello,

I am following up on our quotation request sent earlier this week.
Could you confirm whether 500 g of Benzyl alcohol, CAS 100-51-6, is currently in stock?

Our internal reference is PO-TEST-1042.

Best regards,
Alex Morgan
Northstar Research LLC`,
  },
  {
    id: "NON_INQUIRY",
    label: "非询单邮件",
    purpose: "新闻简报内容，测试非询单过滤",
    subject: "October laboratory technology newsletter",
    body: `Hello,

This is a sample newsletter announcing an upcoming laboratory technology webinar and industry report.

There is no request for quotation, product requirement, order, or customer follow-up in this message.

Newsletter Operations`,
  },
  {
    id: "BILINGUAL_RFQ",
    label: "中英双语询价",
    purpose: "中文主体和英文产品字段",
    subject: "询价 Request for quotation - 苯甲醇 100-51-6",
    body: `您好，

我们需要采购以下产品，请提供报价和交期：

产品 / Product: 苯甲醇 / Benzyl alcohol
CAS: 100-51-6
数量 / Quantity: 2 kg
纯度 / Purity: 99.5% 以上
收货地 / Destination: 中国上海

请同时提供 COA、SDS 和包装规格。

谢谢，
李明
启明生物科技`,
  },
] as const;

export function canUseDevTesterTestMail(
  workspaceCode: string | undefined,
  hostname: string,
): boolean {
  return (
    workspaceCode?.trim().toUpperCase() === "DEVTESTER" &&
    hostname === "127.0.0.1"
  );
}

export function generateDevTesterMail(
  scenario: TestMailScenario,
  now = Date.now(),
): GeneratedTestMail {
  const reference = `DEVTEST-${now.toString(36).toUpperCase()}`;
  return {
    reference,
    subject: `[${reference}] ${scenario.subject}`,
    body: `${scenario.body}\n\n---\nCRM test reference: ${reference}\nScenario: ${scenario.id}`,
  };
}

export function buildGmailComposeUrl(
  recipient: string,
  message: Pick<GeneratedTestMail, "subject" | "body">,
): string {
  const url = new URL("https://mail.google.com/mail/");
  url.searchParams.set("view", "cm");
  url.searchParams.set("fs", "1");
  url.searchParams.set("to", recipient);
  url.searchParams.set("su", message.subject);
  url.searchParams.set("body", message.body);
  return url.href;
}

export function DevTesterTestMailWorkspace({
  workspaceCode,
  hostname = window.location.hostname,
}: {
  workspaceCode?: string;
  hostname?: string;
}) {
  const [recipient, setRecipient] = useState("");
  const [loadingMailbox, setLoadingMailbox] = useState(true);
  const [mailboxError, setMailboxError] = useState("");
  const [scenarioId, setScenarioId] =
    useState<TestMailScenarioId>("STANDARD_RFQ");
  const [generatedAt, setGeneratedAt] = useState(() => Date.now());
  const allowed = canUseDevTesterTestMail(workspaceCode, hostname);
  const scenario =
    DEVTESTER_TEST_MAIL_SCENARIOS.find((item) => item.id === scenarioId) ??
    DEVTESTER_TEST_MAIL_SCENARIOS[0];
  const message = useMemo(
    () => generateDevTesterMail(scenario, generatedAt),
    [generatedAt, scenario],
  );

  useEffect(() => {
    if (!allowed) return;
    let active = true;
    setLoadingMailbox(true);
    setMailboxError("");
    void loadGmailMailboxes()
      .then((mailboxes) => {
        if (!active) return;
        const mailbox = mailboxes.find(
          (item) =>
            item.status === "ACTIVE" &&
            item.authorizationStatus === "AUTHORIZED",
        );
        setRecipient(mailbox?.emailAddress ?? "");
      })
      .catch(() => {
        if (active) setMailboxError("当前无法读取 Gmail 邮箱状态。");
      })
      .finally(() => {
        if (active) setLoadingMailbox(false);
      });
    return () => {
      active = false;
    };
  }, [allowed]);

  if (!allowed) return null;

  const openGmail = () => {
    if (!recipient) return;
    window.open(
      buildGmailComposeUrl(recipient, message),
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <section
      className="devtester-mail-workspace"
      aria-labelledby="devtester-mail-title"
    >
      <header className="devtester-mail-workspace-header">
        <div>
          <span>DEVTESTER · 开发者工具</span>
          <h1 id="devtester-mail-title">发送测试邮件</h1>
        </div>
        <button
          type="button"
          onClick={() => setGeneratedAt(Date.now())}
          title="生成新测试编号"
        >
          <IconRefresh size={16} aria-hidden="true" />
          生成新邮件
        </button>
      </header>

      <div className="devtester-mail-composer">
        <div className="devtester-mail-fields">
          <label>
            <span>邮件性质</span>
            <select
              value={scenarioId}
              onChange={(event) => {
                setScenarioId(event.target.value as TestMailScenarioId);
                setGeneratedAt(Date.now());
              }}
            >
              {DEVTESTER_TEST_MAIL_SCENARIOS.map((item) => (
                <option key={item.id} value={item.id}>
                  {item.label} - {item.purpose}
                </option>
              ))}
            </select>
          </label>
          <div className="devtester-mail-field-row">
            <label>
              <span>收件邮箱</span>
              <input
                value={loadingMailbox ? "正在读取 Gmail 邮箱…" : recipient}
                readOnly
              />
            </label>
            <label>
              <span>测试编号</span>
              <input value={message.reference} readOnly />
            </label>
          </div>
          <label>
            <span>主题</span>
            <input value={message.subject} readOnly />
          </label>
          <label>
            <span>正文预览</span>
            <textarea value={message.body} readOnly rows={15} />
          </label>
        </div>

        <footer>
          <span role={mailboxError ? "alert" : "status"}>
            {mailboxError ||
              (!loadingMailbox && !recipient
                ? "Gmail 邮箱尚未完成只读授权。"
                : "发件账号由当前 Gmail 登录账号决定")}
          </span>
          <button
            className="devtester-mail-primary"
            type="button"
            disabled={loadingMailbox || !recipient}
            onClick={openGmail}
          >
            <IconExternalLink size={16} aria-hidden="true" />在 Gmail 中打开
          </button>
        </footer>
      </div>
    </section>
  );
}
